import { ArticleViewer } from '@wix/kb-article-viewer';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useRef } from 'react';

import { BI } from '../../../constants';
import { useBI } from '../../../hooks/useBI';
import useYouTubePlayers from '../../../hooks/useYouTubePlayers';
import { ArticleWithComponents } from '../../../types';
import { useArticleContentBi } from '../bi';

type ArticleViewerWrapperProps = {
  article: ArticleWithComponents;
  onLinkClick: (e: MouseEvent, href: string) => void;
  onTabClick: (selectedTab: number, currentTabSlugs: string[]) => void;
  maybeGetSelectedTabsFromURL: () => string[];
};

export const ArticleViewerWrapper: FunctionComponent<
  ArticleViewerWrapperProps
> = ({
  article,
  onLinkClick,
  onTabClick,
  maybeGetSelectedTabsFromURL,
}: ArticleViewerWrapperProps) => {
  const { id: articleId, type, components } = article;
  const { locale } = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  useYouTubePlayers(containerRef, articleId);

  const { onImageClick, onTabHeadingClickBi, sectionInteractionBi } =
    useArticleContentBi(articleId, type);
  const { sendBIEvent } = useBI();

  useEffect(() => {
    const handleBIEvents = () => {
      const collapsibleComponents = document.querySelectorAll(
        BI.SELECTORS.COLLAPSIBLE_COMPONENTS
      );

      const imagesComponents = document.querySelectorAll(
        BI.SELECTORS.CLICKABLE_IMAGES
      );

      const tabsComponentsHeadings = document.querySelectorAll(
        BI.SELECTORS.TABS
      );

      tabsComponentsHeadings.forEach((heading) => {
        heading.addEventListener('click', onTabHeadingClickBi);
      });

      imagesComponents.forEach((img) =>
        img.addEventListener('click', onImageClick)
      );

      if (collapsibleComponents) {
        collapsibleComponents.forEach((collapsibleComponent) => {
          collapsibleComponent.addEventListener('click', sectionInteractionBi);
        });
      }

      return () => {
        if (collapsibleComponents) {
          collapsibleComponents.forEach((collapsibleComponent) => {
            collapsibleComponent.removeEventListener(
              'click',
              sectionInteractionBi
            );
          });
        }
        if (imagesComponents) {
          imagesComponents.forEach((imagComp) => {
            imagComp.removeEventListener('click', onImageClick);
          });
        }
        if (tabsComponentsHeadings) {
          tabsComponentsHeadings.forEach((tabComp) => {
            tabComp.removeEventListener('click', onTabHeadingClickBi);
          });
        }
      };
    };

    return handleBIEvents();
  }, [articleId, locale, type, sendBIEvent, onImageClick]);

  return (
    <div ref={containerRef}>
      <ArticleViewer
        key={articleId}
        components={components}
        locale={locale as string}
        onLinkClick={onLinkClick}
        tabsProps={{
          onTabClick,
          selectedTabsOnLoad: maybeGetSelectedTabsFromURL(),
        }}
        theme="helpcenter"
      />
    </div>
  );
};
